import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import api, { formatApiError } from "../lib/api";
import { fmt12 } from "../lib/utils";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Calendar, Clock, MapPin, ShieldCheck, Bell, Award, Trophy, CalendarPlus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [shifts, setShifts] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [waiver, setWaiver] = useState(null);
  const [hours, setHours] = useState(null);
  const [busyShift, setBusyShift] = useState(null);
  const [quickAdd, setQuickAdd] = useState(null);

  const load = async () => {
    const [s, n, w, h] = await Promise.all([
      api.get("/shifts"),
      api.get("/notifications"),
      api.get("/waiver/me"),
      api.get("/volunteer/hours"),
    ]);
    setShifts(s.data.filter((x) => x.is_signed_up));
    setNotifs(n.data);
    setWaiver(w.data);
    setHours(h.data);
    api.post("/notifications/mark-read").catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const cancel = async (shift_id) => {
    try {
      await api.delete(`/shifts/signup/${shift_id}`);
      toast.success("Shift cancelled.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doCheckIn = async (shift_id) => {
    setBusyShift(shift_id);
    try {
      await api.post("/shifts/checkin", { shift_id });
      toast.success("Checked in. Have a great shift!");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyShift(null); }
  };

  const doCheckOut = async (shift_id) => {
    setBusyShift(shift_id);
    try {
      const { data } = await api.post("/shifts/checkout", { shift_id });
      toast.success(`Checked out — ${data.hours} hours logged!`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyShift(null); }
  };

  const addToCalendar = (shift) => setQuickAdd(shift);

  // Direct-add URL helpers
  const _dt = (d, t) => `${d.replace(/-/g,"")}T${(t||"00:00").replace(":","")}00`;
  const _text = (s) => `${s.title}${s.role ? " — " + s.role : ""}`.trim();
  const _details = (s) => [s.description, "Hood River Adopt A Dog volunteer shift."].filter(Boolean).join("\n");
  const _loc = (s) => s.location || "3910 Heron Drive, Hood River, OR 97031";
  const googleAddUrl = (s) => {
    const p = new URLSearchParams({
      action: "TEMPLATE", text: _text(s),
      dates: `${_dt(s.date, s.start_time)}/${_dt(s.date, s.end_time)}`,
      details: _details(s), location: _loc(s), ctz: "America/Los_Angeles",
    });
    return `https://calendar.google.com/calendar/render?${p.toString()}`;
  };
  const outlookAddUrl = (s) => {
    const p = new URLSearchParams({
      path: "/calendar/action/compose", rru: "addevent", subject: _text(s),
      startdt: `${s.date}T${s.start_time}:00`, enddt: `${s.date}T${s.end_time}:00`,
      location: _loc(s), body: _details(s),
    });
    return `https://outlook.live.com/calendar/0/deeplink/compose?${p.toString()}`;
  };
  const appleAddUrl = (s) => `${process.env.REACT_APP_BACKEND_URL}/api/calendar/shift/${s.shift_id}.ics`;

  const today = new Date().toISOString().split("T")[0];
  const upcoming = shifts.filter((s) => s.date >= today);
  const past = shifts.filter((s) => s.date < today);

  return (
    <div className="min-h-screen bg-[color:var(--cream)]">
      <Navbar />
      <div className="max-w-6xl mx-auto px-5 lg:px-10 py-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
          <div>
            <div className="tag-pill tag-yellow">Volunteer Dashboard</div>
            <h1 className="font-display text-4xl sm:text-5xl mt-3">Hi {user?.name?.split(" ")[0] || "friend"} 👋</h1>
            <p className="text-[color:var(--stone)] mt-2">Here&rsquo;s what&rsquo;s coming up, and how the pack&rsquo;s doing.</p>
          </div>
          <Link to="/shifts" data-testid="dashboard-browse-shifts-btn" className="btn-pill btn-primary text-sm">Browse open shifts</Link>
        </div>

        {/* Stats row */}
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          <div className={`card-warm p-6 flex items-center gap-4 ${!waiver?.signed && "ring-2 ring-[color:var(--red)]/30"}`} data-testid="dashboard-waiver-card">
            <div className={`h-12 w-12 rounded-full flex items-center justify-center ${waiver?.signed ? "bg-[#E4EBDD] text-[#2D4A3E]" : "bg-[color:var(--red)]/15 text-[color:var(--red)]"}`}>
              <ShieldCheck className="h-6 w-6" />
            </div>
            <div className="flex-1">
              <div className="font-display text-base">{waiver?.signed ? "Waiver signed" : "Waiver required"}</div>
              <div className="text-xs text-[color:var(--stone)]">{waiver?.signed ? "You’re cleared to volunteer." : "Sign to unlock sign-ups."}</div>
            </div>
            {!waiver?.signed && <button data-testid="dashboard-sign-waiver-btn" onClick={()=>nav("/waiver")} className="btn-pill btn-primary text-xs">Sign</button>}
          </div>

          <div className="card-warm p-6" data-testid="dashboard-hours-card">
            <div className="flex items-center gap-3 mb-1">
              <div className="h-10 w-10 rounded-full bg-[color:var(--yellow)]/60 flex items-center justify-center">
                <Trophy className="h-5 w-5" />
              </div>
              <div className="font-display text-3xl">{hours?.total_hours ?? 0}</div>
            </div>
            <div className="text-xs text-[color:var(--stone)] uppercase tracking-wide">Hours logged</div>
            {hours?.tier && <div className="mt-2 tag-pill tag-forest !text-[10px]" data-testid="dashboard-tier-badge">{hours.tier}</div>}
          </div>

          <div className="card-warm p-6 flex items-center gap-4">
            <div className="h-12 w-12 rounded-full bg-[color:var(--cream-2)] flex items-center justify-center">
              <Award className="h-6 w-6" />
            </div>
            <div>
              <div className="font-display text-3xl">{shifts.length}</div>
              <div className="text-xs text-[color:var(--stone)] uppercase tracking-wide">Shifts signed up</div>
            </div>
          </div>

          <div className="card-warm p-6 flex items-center gap-4">
            <div className="h-12 w-12 rounded-full bg-[color:var(--red)]/15 flex items-center justify-center text-[color:var(--red)]">
              <Bell className="h-6 w-6" />
            </div>
            <div>
              <div className="font-display text-3xl">{notifs.length}</div>
              <div className="text-xs text-[color:var(--stone)] uppercase tracking-wide">Notifications</div>
            </div>
          </div>
        </div>

        {/* Upcoming shifts */}
        <h2 className="font-display text-2xl mb-4">Upcoming shifts</h2>
        {upcoming.length === 0 ? (
          <div className="card-warm p-8 text-center text-[color:var(--stone)] mb-8" data-testid="dashboard-no-upcoming">
            You have no upcoming shifts. <Link to="/shifts" className="text-[color:var(--red)] font-semibold">Find one →</Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4 mb-10">
            {upcoming.map((s) => {
              const isToday = s.date === today;
              return (
                <div
                  key={s.shift_id}
                  role="button"
                  tabIndex={0}
                  onClick={() => addToCalendar(s)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); addToCalendar(s); } }}
                  className="card-warm p-6 cursor-pointer transition hover:shadow-md hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-[color:var(--red)]/40"
                  data-testid={`upcoming-shift-${s.shift_id}`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="font-display text-lg">{s.title}</div>
                    <span className="tag-pill tag-forest">{s.role}</span>
                  </div>
                  <div className="text-sm text-[color:var(--stone)] space-y-1">
                    <div className="flex items-center gap-2"><Calendar className="h-4 w-4" /> {s.date}{isToday && <span className="tag-pill tag-red !text-[10px] !py-0 ml-2">Today</span>}</div>
                    <div className="flex items-center gap-2"><Clock className="h-4 w-4" /> {fmt12(s.start_time)} – {fmt12(s.end_time)}</div>
                    <div className="flex items-center gap-2"><MapPin className="h-4 w-4" /> {s.location}</div>
                  </div>
                  <div className="mt-4 flex items-center gap-2 flex-wrap" onClick={(e) => e.stopPropagation()}>
                    <span className="tag-pill tag-forest !text-[11px]" data-testid={`autolog-${s.shift_id}`}>Hours auto-log after the shift</span>
                    <button data-testid={`cancel-signup-${s.shift_id}`} onClick={() => cancel(s.shift_id)} className="text-xs font-semibold text-[color:var(--red)] hover:underline ml-auto">Cancel signup</button>
                  </div>
                  <div className="mt-3 pt-3 border-t border-[color:var(--border)]/50 flex items-center gap-1.5 text-xs font-semibold text-[color:var(--red)]">
                    <CalendarPlus className="h-3.5 w-3.5" />
                    <span data-testid={`add-to-calendar-${s.shift_id}`}>Tap card to add to my calendar →</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Notifications */}
        <h2 id="notifications" className="font-display text-2xl mb-4">Notifications</h2>
        {notifs.length === 0 ? (
          <div className="card-warm p-6 text-sm text-[color:var(--stone)]" data-testid="dashboard-no-notifs">No notifications yet.</div>
        ) : (
          <div className="space-y-3 mb-10">
            {notifs.map((n) => (
              <div key={n.notif_id} className="card-warm p-5" data-testid={`notif-${n.notif_id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="font-display text-base">{n.title}</div>
                    <div className="text-sm text-[color:var(--stone)] mt-1 whitespace-pre-wrap">{n.body}</div>
                  </div>
                  <div className="text-xs text-[color:var(--stone)] shrink-0">{new Date(n.created_at).toLocaleString()}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {past.length > 0 && (
          <>
            <h2 className="font-display text-2xl mb-4">Past shifts</h2>
            <div className="grid md:grid-cols-3 gap-3 opacity-80">
              {past.slice(0, 6).map((s) => (
                <div key={s.shift_id} className="card-warm p-4 text-sm" data-testid={`past-shift-${s.shift_id}`}>
                  <div className="font-semibold">{s.title}</div>
                  <div className="text-xs text-[color:var(--stone)] mt-1">{s.date} · {s.role}</div>
                  {s.my_hours != null && <div className="mt-2 tag-pill tag-forest !text-[11px]">{s.my_hours}h logged</div>}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Quick-add calendar dialog (one-tap, no download) */}
      <Dialog open={!!quickAdd} onOpenChange={(v) => !v && setQuickAdd(null)}>
        <DialogContent data-testid="dashboard-quick-add-dialog" className="rounded-2xl max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Add this shift to your calendar</DialogTitle>
            <DialogDescription className="text-[color:var(--ink)] text-base pt-1">
              {quickAdd && (
                <div className="text-sm">
                  <div className="font-semibold">{quickAdd.title}</div>
                  <div className="text-[color:var(--stone)]">{quickAdd.date} · {fmt12(quickAdd.start_time)}–{fmt12(quickAdd.end_time)}</div>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 pt-1">
            {quickAdd && (
              <>
                <a data-testid="dashboard-quick-google" href={googleAddUrl(quickAdd)} target="_blank" rel="noreferrer"
                   onClick={() => { toast.success("Opening Google Calendar…"); setTimeout(() => setQuickAdd(null), 400); }}
                   className="w-full btn-pill btn-primary text-sm inline-flex items-center justify-center gap-2 py-3">
                  <svg viewBox="0 0 24 24" className="h-5 w-5"><path fill="currentColor" d="M21.35 11.1H12v2.9h5.35c-.23 1.5-1.7 4.4-5.35 4.4-3.22 0-5.85-2.67-5.85-5.95 0-3.28 2.63-5.95 5.85-5.95 1.83 0 3.06.78 3.76 1.45l2.56-2.47C16.86 3.9 14.68 3 12 3 6.98 3 2.93 7.03 2.93 12s4.05 9 9.07 9c5.24 0 8.71-3.68 8.71-8.86 0-.6-.06-1.05-.15-1.5z"/></svg>
                  Add to Google Calendar
                </a>
                <a data-testid="dashboard-quick-apple" href={appleAddUrl(quickAdd)}
                   onClick={() => { toast.success("Opening Calendar app…"); setTimeout(() => setQuickAdd(null), 400); }}
                   className="w-full btn-pill btn-ghost-ink text-sm inline-flex items-center justify-center gap-2 py-3">
                  <svg viewBox="0 0 24 24" className="h-5 w-5"><path fill="currentColor" d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>
                  Add to Apple Calendar (iPhone / Mac)
                </a>
                <a data-testid="dashboard-quick-outlook" href={outlookAddUrl(quickAdd)} target="_blank" rel="noreferrer"
                   onClick={() => { toast.success("Opening Outlook…"); setTimeout(() => setQuickAdd(null), 400); }}
                   className="w-full btn-pill btn-ghost-ink text-sm inline-flex items-center justify-center gap-2 py-3">
                  <svg viewBox="0 0 24 24" className="h-5 w-5"><path fill="currentColor" d="M21.5 6.5v11c0 .83-.67 1.5-1.5 1.5H10v-2h9v-9L13 4l-3 2V4l3-2 8.5 4.5zM8 8h1v8H8V8zm-3 2h1v4H5v-4zm5-2h5v8h-5V8z"/></svg>
                  Add to Outlook Calendar
                </a>
                <div className="text-xs text-[color:var(--stone)] text-center pt-1">
                  Your calendar opens with this shift ready to save — one tap and you&rsquo;re done.
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <button data-testid="dashboard-quick-close" onClick={() => setQuickAdd(null)} className="btn-pill btn-ghost-ink text-sm">Close</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
